import { useCallback, useEffect, useState } from 'react';

/**
 * Local persistence for the Coaches Hub guided onboarding.
 * Everything here is UI state only — no backend writes.
 */

const TOUR_KEY = 'sportara-coach-tour-status'; // 'pending' | 'skipped' | 'done'
const FLAG_KEY = 'sportara-coach-tour-flags';
const BADGE_KEY = 'sportara-coach-ready-badge';

export type TourStatus = 'pending' | 'skipped' | 'done';

export interface CoachTourFlags {
  codeShared: boolean;
  analyticsVisited: boolean;
}

const DEFAULT_FLAGS: CoachTourFlags = { codeShared: false, analyticsVisited: false };

function readStatus(): TourStatus {
  try {
    const v = localStorage.getItem(TOUR_KEY);
    if (v === 'skipped' || v === 'done') return v;
  } catch {
    /* ignore */
  }
  return 'pending';
}

function readFlags(): CoachTourFlags {
  try {
    const raw = localStorage.getItem(FLAG_KEY);
    if (raw) return { ...DEFAULT_FLAGS, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return DEFAULT_FLAGS;
}

const FLAGS_EVENT = 'sportara-coach-tour-flags-changed';

/** Mark a checklist flag from anywhere in the coach UI. */
export function setCoachTourFlag(key: keyof CoachTourFlags) {
  try {
    const next = { ...readFlags(), [key]: true };
    localStorage.setItem(FLAG_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(FLAGS_EVENT));
  } catch {
    /* ignore */
  }
}

export function hasReadyBadge(): boolean {
  try {
    return localStorage.getItem(BADGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function grantReadyBadge() {
  try {
    localStorage.setItem(BADGE_KEY, 'true');
  } catch {
    /* ignore */
  }
}

export function useCoachTour() {
  const [status, setStatus] = useState<TourStatus>(() => readStatus());
  const [flags, setFlags] = useState<CoachTourFlags>(() => readFlags());

  useEffect(() => {
    const sync = () => setFlags(readFlags());
    window.addEventListener(FLAGS_EVENT, sync);
    return () => window.removeEventListener(FLAGS_EVENT, sync);
  }, []);

  const persist = useCallback((next: TourStatus) => {
    try {
      localStorage.setItem(TOUR_KEY, next);
    } catch {
      /* ignore */
    }
    setStatus(next);
  }, []);

  return {
    status,
    flags,
    /** true the very first time a coach lands in Coaches Hub */
    shouldAutoStart: status === 'pending',
    complete: () => persist('done'),
    skip: () => persist('skipped'),
    reset: () => persist('pending'),
  };
}
